import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Loader2, Film } from "lucide-react";
import { api, formatApiError, API } from "../lib/api";
import PrereqChecklist from "./render/PrereqChecklist";
import RenderPreviewCards from "./render/RenderPreviewCards";
import JobStatusCard from "./render/JobStatusCard";
import LastTerminalNotice from "./render/LastTerminalNotice";
import JobHistory from "./render/JobHistory";
import VoiceStylePicker from "./voiceover/VoiceStylePicker";
import MusicPanel from "./MusicPanel";

const ACTIVE_STATES = new Set(["queued", "validating", "preparing_assets", "rendering", "verifying"]);
const TERMINAL = new Set(["completed", "failed", "cancelled"]);
// Only completed jobs deserve the full status card (they show the player +
// download). In-progress jobs also show it. Cancelled / failed get a calm
// banner instead (see LastTerminalNotice) and live in the history list.
const PROMOTABLE = new Set([...ACTIVE_STATES, "completed"]);
const normalizeJob = (job) => job
  ? { ...job, status: String(job.status || "").toLowerCase() }
  : job;

export default function RenderPanel({
  projectId, project, script, scenes, metadata, assets, timing, canEdit, onChange,
}) {
  const [preflight, setPreflight] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [fitting, setFitting] = useState(false);
  const [voiceStyle, setVoiceStyle] = useState(project?.voice_style || project?.voice_id || "narrator");
  const [burnSubtitles, setBurnSubtitles] = useState(false);
  // was: aspect_ratio: "9:16"
  const [aspect, setAspect] = useState(project?.aspect_ratio || "9:16");
  const pollRef = useRef(null);

  useEffect(() => {
    if (project?.voice_style || project?.voice_id) setVoiceStyle(project.voice_style || project.voice_id);
    if (project?.aspect_ratio) setAspect(project.aspect_ratio);
    if (typeof project?.burn_subtitles === "boolean") setBurnSubtitles(project.burn_subtitles);
  }, [project?.voice_style, project?.voice_id, project?.aspect_ratio, project?.burn_subtitles]);

  const refreshAll = async () => {
    try {
      const [pf, jl] = await Promise.all([
        api.get(`/projects/${projectId}/render/preflight`),
        api.get(`/projects/${projectId}/render/jobs`),
      ]);
      const normalizedJobs = (jl.data || []).map(normalizeJob);
      setPreflight(pf.data);
      setJobs(normalizedJobs);
      const active = normalizedJobs.find((j) => ACTIVE_STATES.has(j.status));
      const latest = normalizedJobs[0] || null;
      // Only show the in-detail JobStatusCard for active or completed jobs.
      // Cancelled / failed jobs surface as a calm banner via LastTerminalNotice
      // and remain accessible in the JobHistory list below.
      setActiveJob(active || (latest && PROMOTABLE.has(latest.status) ? latest : null));
    } catch (err) {
      toast.error("Failed to load render data", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    }
  };

  useEffect(() => {
    refreshAll();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll while a job is active
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!activeJob || !ACTIVE_STATES.has(String(activeJob.status || "").toLowerCase())) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/render/jobs/${activeJob.id}`);
        const normalizedJob = normalizeJob(data);
        if (TERMINAL.has(normalizedJob.status)) {
          clearInterval(pollRef.current);
          // Only keep the active card for completed (player + download).
          // Cancelled/failed drop out so LastTerminalNotice + history take over.
          setActiveJob(PROMOTABLE.has(normalizedJob.status) ? normalizedJob : null);
          const [pf, jl, full] = await Promise.all([
            api.get(`/projects/${projectId}/render/preflight`),
            api.get(`/projects/${projectId}/render/jobs`),
            api.get(`/projects/${projectId}`),
          ]);
          setPreflight(pf.data);
          setJobs((jl.data || []).map(normalizeJob));
          onChange(full.data);
          if (normalizedJob.status === "completed") {
            toast.success("Render complete", { description: "MP4 ready below." });
          } else if (normalizedJob.status === "failed") {
            toast.error("Render failed", { description: normalizedJob.error_message || "Unknown error" });
          } else if (normalizedJob.status === "cancelled") {
            toast("Render cancelled");
          }
        } else {
          setActiveJob(normalizedJob);
        }
      } catch {/* silent retry */}
    }, 2500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.id, activeJob?.status]);

  const getAudioDuration = (url) => new Promise((resolve) => {
    if (!url) return resolve(0);
    const audio = new Audio();
    audio.preload = "metadata";
    audio.onloadedmetadata = () => resolve(audio.duration || 0);
    audio.onerror = () => resolve(0);
    audio.src = url;
    setTimeout(() => resolve(0), 5000);
  });

  const start = async () => {
    setStarting(true);
    // Pre-render validation: VO vs scenes total mismatch
    try {
      const voAssets = (assets || []).filter(a => a.asset_type === "voiceover_audio" && a.status !== "rejected");
      const fullVO = voAssets.find(a => !a.scene_id && a.id === project.selected_voiceover_asset_id);
      let voDuration = 0;
      if (fullVO?.duration) voDuration = parseFloat(fullVO.duration);
      else if (voAssets.filter(a => a.scene_id).length > 0) {
        const byScene = {};
        voAssets.filter(a => a.scene_id).forEach(v => {
          if (!byScene[v.scene_id] || String(v.created_at) > String(byScene[v.scene_id].created_at)) byScene[v.scene_id] = v;
        });
        voDuration = Object.values(byScene).reduce((sum, v) => sum + parseFloat(v.duration || 0), 0);
        // If duration not in asset, try to probe via audio element
        if (!voDuration) {
          for (const v of Object.values(byScene)) {
            // eslint-disable-next-line no-await-in-loop
            const d = await getAudioDuration(v.preview_url || v.url);
            voDuration += d;
          }
        }
      } else if (fullVO?.preview_url) {
        voDuration = await getAudioDuration(fullVO.preview_url);
      }
      const scenesTotal = (scenes || []).reduce((sum, s) => sum + parseFloat(s.duration || 30), 0);
      const target = parseInt(project?.target_duration || 0, 10);
      const mismatch = Math.abs(voDuration - scenesTotal);
      if (voDuration && scenesTotal && mismatch > 5) {
        // VO is master: backend _build_subclip_plan() scales video to VO
        // exactly (render.py). Warn only — do not block. Video will match VO.
        toast.warning("Duration auto-alignment: video will match VO", {
          description: `Target ${target}s, VO ${voDuration.toFixed(1)}s, Scenes ${scenesTotal.toFixed(1)}s — Δ ${mismatch.toFixed(1)}s. VO is master, video will be scaled to ${voDuration.toFixed(1)}s.`,
          duration: 8000,
        });
        console.warn("[Render] VO vs Scenes mismatch — auto-aligning to VO", { target, voDuration, scenesTotal, mismatch, projectId });
      }
    } catch (e) {
      console.warn("[Render] Preflight validation failed", e);
    }
    try {
      const { data } = await api.post(`/projects/${projectId}/render/start`, { voice_style: voiceStyle, voice_id: voiceStyle, burn_subtitles: burnSubtitles, aspect_ratio: aspect });
      setActiveJob(normalizeJob(data));
      toast.success("Render queued");
      const jl = await api.get(`/projects/${projectId}/render/jobs`);
      setJobs((jl.data || []).map(normalizeJob));
      console.log("[Render] Queued", data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail) : String(detail || err.message);
      console.error("[Render] Start failed", err, detail, err.stack);
      if (typeof detail === "object" && detail?.issues) {
        toast.error("Cannot start render", { description: detail.issues.join(" · ") + ` | VO vs Scenes check: ${msg}` });
      } else if (err.response?.status === 409) {
        toast.error("Render already running", { description: msg });
      } else {
        toast.error("Start failed: something went wrong please try again", { description: formatApiError(detail) || err.message + ` | ${msg}` });
      }
      // Surface server logs
      try {
        const pre = await api.get(`/projects/${projectId}/render/preflight`);
        console.log("[Render] Preflight", pre.data);
        if (!pre.data.ok) toast.error(`Preflight: ${pre.data.issues?.join(", ")}`);
      } catch {}
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!activeJob) return;
    setCancelling(true);
    try {
      await api.post(`/projects/${projectId}/render/jobs/${activeJob.id}/cancel`);
      toast("Cancelling…");
    } catch (err) {
      toast.error("Cancel failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setCancelling(false);
    }
  };

  if (!preflight) {
    return (
      <div className="p-8 text-sm text-zinc-500 font-mono">Loading render state…</div>
    );
  }

  const canRender = preflight.ok && !ACTIVE_STATES.has(String(activeJob?.status || "").toLowerCase() || "");
  const completed = String(activeJob?.status || "").toLowerCase() === "completed";
  const inProgress = activeJob && ACTIVE_STATES.has(String(activeJob.status || "").toLowerCase());

  const selectedThumbnail = (assets || []).find(
    (a) => a.id === project.selected_thumbnail_asset_id && a.asset_type === "generated_thumbnail"
  );
  const selectedVoice = (assets || []).find(
    (a) => a.id === project.selected_voiceover_asset_id && a.asset_type === "voiceover_audio"
  );

  return (
    <div className="space-y-6">
      {timing && (
        <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-sm p-4" data-testid="timing-contract">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E5FF] mb-3">
            Selected duration controls the final video
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="block text-zinc-500 text-xs">Final</span>{Number(timing.target_seconds).toFixed(1)}s</div>
            <div><span className="block text-zinc-500 text-xs">Intro</span>{Number(timing.intro_seconds).toFixed(1)}s</div>
            <div><span className="block text-zinc-500 text-xs">Narration + scenes</span>{Number(timing.narration_seconds).toFixed(1)}s</div>
            <div><span className="block text-zinc-500 text-xs">Voiceover</span>{timing.voiceover_seconds ? `${Number(timing.voiceover_seconds).toFixed(1)}s` : "Not generated"}</div>
          </div>
          {(!timing.scenes_match || timing.voiceover_action === "rebuild") && (
            <p className="font-mono text-[10px] text-amber-300 mt-3">
              {!timing.scenes_match && <>Stored scene timing will be repaired to {Number(timing.narration_seconds).toFixed(1)}s. </>}
              {timing.voiceover_action === "rebuild" && "Regenerate the script and voiceover before rendering."}
            </p>
          )}
          {timing.voiceover_action === "tempo_fit" && (
            <p className="font-mono text-[10px] text-zinc-400 mt-3">
              Voiceover is within 5% and will be tempo-fitted to the narration budget.
            </p>
          )}
        </div>
      )}
      <PrereqChecklist preflight={preflight} onRefresh={refreshAll} />

      {(preflight.scene_count !== undefined || preflight.attached_clip_count !== undefined) && (
        <div
          data-testid="render-coverage"
          className="border border-zinc-800 bg-[#121212] rounded-sm px-4 py-3"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            Scene coverage
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="block text-zinc-500 text-xs">Scenes</span>
              {Number(preflight.scene_count || 0)}
            </div>
            <div>
              <span className="block text-zinc-500 text-xs">Clips attached</span>
              {Number(preflight.attached_clip_count || 0)}
            </div>
            <div>
              <span className="block text-zinc-500 text-xs">Scene coverage</span>
              {Math.round(Number(preflight.scene_coverage || 0) * 100)}%
            </div>
            <div>
              <span className="block text-zinc-500 text-xs">Candidates / orphaned</span>
              {Number(preflight.asset_candidate_count || 0)} / {Number(preflight.orphaned_clip_count || 0)}
            </div>
          </div>
          <p className="font-mono text-[10px] text-zinc-600 mt-2">
            Clips attached is a count of files, not a fraction of scenes. Orphaned clips belong to
            an older scene plan and are ignored.
          </p>
        </div>
      )}

      {canEdit && preflight.autofit_suggestion && (
        <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-sm px-4 py-3 flex items-center gap-3 flex-wrap">
          <p className="flex-1 text-sm text-zinc-300 min-w-[220px]">
            Narration runs {preflight.autofit_suggestion.voiceover_seconds?.toFixed(1)}s — apply a{" "}
            {preflight.autofit_suggestion.from}s → {preflight.autofit_suggestion.to}s duration fit?
          </p>
          <button
            data-testid="autofit-duration-btn"
            disabled={fitting}
            onClick={async () => {
              setFitting(true);
              try {
                const { data } = await api.post(`/projects/${projectId}/render/autofit-duration`);
                toast.success(`Duration fitted to ${data.autofit.to}s`);
                await refreshAll();
                const full = await api.get(`/projects/${projectId}`);
                onChange(full.data);
              } catch (err) {
                toast.error("Auto-fit failed", { description: formatApiError(err.response?.data?.detail) || err.message });
              } finally {
                setFitting(false);
              }
            }}
            className="font-mono text-[10px] uppercase tracking-widest bg-[#00E5FF] text-black px-3 py-2 rounded-sm hover:bg-[#33EFFF] disabled:opacity-50"
          >
            {fitting ? "Fitting…" : `Fit to ${preflight.autofit_suggestion.to}s`}
          </button>
        </div>
      )}

      {canEdit && (
        <div className="border border-zinc-800 bg-[#121212] rounded-sm p-4 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Narrator voice — change until render</div>
            <VoiceStylePicker value={voiceStyle} onChange={setVoiceStyle} />
            <p className="font-mono text-[10px] text-zinc-600 mt-2">Voice selector stays enabled right up to the Render button, not locked after Part generation.</p>
          </div>
          <MusicPanel
            projectId={projectId}
            project={project}
            assets={assets}
            canEdit={canEdit}
            onProjectChange={async (view) => { await refreshAll(); onChange(view); }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-zinc-800">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Aspect ratio</div>
              <select value={aspect} onChange={e=>setAspect(e.target.value)} className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-2 text-sm rounded-sm focus:border-[#00E5FF]">
                <option value="9:16">9:16 Tall - TikTok / Reels / Shorts</option>
                <option value="16:9">16:9 Wide - YouTube</option>
                <option value="1:1">1:1 Square - Feed</option>
              </select>
              <p className="font-mono text-[10px] text-zinc-600 mt-1">Choose at render — no need to regenerate.</p>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Captions</div>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={burnSubtitles} onChange={(e) => setBurnSubtitles(e.target.checked)} className="h-4 w-4 accent-[#00E5FF] bg-[#0A0A0A] border-zinc-700 rounded" />
                Burn captions into video
              </label>
              <p className="font-mono text-[10px] text-zinc-600 mt-1">Off = clean video. SRT still saved for CapCut. Platforms auto-caption anyway.</p>
            </div>
          </div>
        </div>
      )}

      <RenderPreviewCards
        selectedThumbnail={selectedThumbnail}
        selectedVoice={selectedVoice}
      />

      {activeJob && (
        <JobStatusCard
          job={activeJob}
          project={project}
          selectedThumbnail={selectedThumbnail}
          canEdit={canEdit}
          cancelling={cancelling}
          starting={starting}
          onCancel={cancel}
          onRetry={start}
          downloadUrl={`${API}/projects/${projectId}/render/jobs/${activeJob.id}/download`}
        />
      )}

      {!activeJob && jobs[0] && (String(jobs[0].status || "").toLowerCase() === "cancelled" || String(jobs[0].status || "").toLowerCase() === "failed") && (
        <LastTerminalNotice
          job={jobs[0]}
          canEdit={canEdit}
          starting={starting}
          onRetry={start}
        />
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3">
        {canEdit && (
          <button
            data-testid="render-start-btn"
            onClick={start}
            disabled={!canRender || starting}
            title={!preflight.ok ? `Missing: ${preflight.issues.join(", ")}` : ""}
            className="flex items-center gap-2 bg-[#00E5FF] text-black font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-[#33EFFF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {starting ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {inProgress ? "Render running…" : completed ? "Render again" : "Start render"}
          </button>
        )}
        <a
          data-testid="export-package-zip"
          href={`${API}/projects/${projectId}/export/package.zip`}
          className="flex items-center gap-2 border border-zinc-800 text-white text-sm px-5 py-2.5 rounded-sm hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
        >
          <Film size={14} /> Export package (ZIP)
        </a>
      </div>

      <JobHistory jobs={jobs} projectId={projectId} />
    </div>
  );
}
